
import { useMemo, useState } from 'react'
import { useReadContract, useChainId } from 'wagmi'
import { isAddress } from 'viem'
import { Search, Copy, ExternalLink, AlertCircle, CheckCircle2, User } from 'lucide-react'
import toast from 'react-hot-toast'
import { NODE_REGISTRY_ADDRESSES, NODE_REGISTRY_ABI, CHAIN_CONFIG } from '@/lib/config'
import { copyToClipboard, getBlockExplorerUrl, parseMultiaddr } from '@/lib/utils'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { useENSName } from '@/hooks/useENS'

export function LookupPage() {
  const [searchAddress, setSearchAddress] = useState('')
  const [queriedAddress, setQueriedAddress] = useState('')
  const chainId = useChainId()

  const contractAddress = NODE_REGISTRY_ADDRESSES[chainId as keyof typeof NODE_REGISTRY_ADDRESSES]

  // ENS reverse resolution
  const { ensName, isLoading: isLoadingENS, hasENS } = useENSName(queriedAddress)

  const {
    data: nodeAddress,
    isLoading,
    isError,
    error,
    refetch
  } = useReadContract({
    address: contractAddress,
    abi: NODE_REGISTRY_ABI,
    functionName: 'nodeOf',
    args: [queriedAddress as `0x${string}`],
    query: {
      enabled: !!queriedAddress && isAddress(queriedAddress),
    },
  })
  const finalNodeAddress = useMemo(() => {
    if (!nodeAddress) return null
    return parseMultiaddr(nodeAddress)
  }, [nodeAddress])

  const handleSearch = () => {
    if (!searchAddress.trim()) {
      toast.error('Please enter an address to lookup')
      return
    }

    if (!isAddress(searchAddress)) {
      toast.error('Please enter a valid Ethereum address')
      return
    }

    if (!contractAddress) {
      toast.error(`Registry contract not deployed on ${CHAIN_CONFIG[chainId as keyof typeof CHAIN_CONFIG]?.name || 'this network'}`)
      return
    }

    setQueriedAddress(searchAddress)
    refetch()
  }

  const handleCopyAddress = async () => {
    try {
      await copyToClipboard(queriedAddress)
      toast.success('Address copied to clipboard')
    } catch (error) {
      toast.error('Failed to copy address')
    }
  }

  const handleCopyNode = async () => {
    if (!finalNodeAddress) return
    try {
      await copyToClipboard(finalNodeAddress)
      toast.success('Node address copied to clipboard')
    } catch (error) {
      toast.error('Failed to copy node address')
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const chainInfo = CHAIN_CONFIG[chainId as keyof typeof CHAIN_CONFIG]

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Node Lookup
        </h2>
        <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Search for node addresses by Ethereum account. Enter any address to see if they have registered a node.
        </p>
      </div>

      {/* Search Form */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-8">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label htmlFor="search-address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ethereum Address
            </label>
            <input

              id="search-address"
              type="text"
              value={searchAddress}
              onChange={(e) => setSearchAddress(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="0x742d35Cc6634C0532925a3b8D6Ac9E62DF3E4FBA"
              className="input-field text-black"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleSearch}
              disabled={isLoading || !searchAddress.trim()}
              className="btn-primary flex items-center space-x-2 w-full sm:w-auto"
            >
              {isLoading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <Search size={18} />
              )}
              <span>Search</span>
            </button>
          </div>
        </div>

        {/* Chain Info */}
        {chainInfo && (
          <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
              <span>Searching on</span>
              <span className="font-medium" style={{ color: chainInfo.color }}>
                {chainInfo.icon} {chainInfo.name}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {queriedAddress && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Search Results
            </h3>
            {chainInfo && (
              <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                <span style={{ color: chainInfo.color }}>{chainInfo.icon}</span>
                <span>{chainInfo.name}</span>
              </div>
            )}
          </div>

          {/* Queried Address */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Address:
              </span>
            </div>
            <div className="flex items-center space-x-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <code className="flex-1 text-sm font-mono text-gray-900 dark:text-gray-100 break-all">
                {queriedAddress}
              </code>
              <button
                onClick={handleCopyAddress}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
                title="Copy address"
              >
                <Copy size={16} />
              </button>
              <a
                href={getBlockExplorerUrl(chainId, queriedAddress)}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
                title="View on block explorer"
              >
                <ExternalLink size={16} />
              </a>
            </div>

            {/* ENS Name */}
            {chainId === 1 && (
              <div className="mt-2">
                {isLoadingENS ? (
                  <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                    <LoadingSpinner size="sm" />
                    <span>Resolving ENS name...</span>
                  </div>
                ) : hasENS ? (
                  <div className="flex items-center space-x-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <User size={16} className="text-blue-500" />
                    <span className="text-sm text-blue-700 dark:text-blue-300 font-medium">
                      {ensName}
                    </span>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    No ENS name found
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Node Address Result */}
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Node Address:
              </span>
            </div>

            {isLoading && (
              <div className="flex items-center space-x-2 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <LoadingSpinner size="sm" />
                <span className="text-blue-700 dark:text-blue-300">
                  Fetching node address...
                </span>
              </div>
            )}

            {isError && (
              <div className="flex items-center space-x-2 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <AlertCircle size={20} className="text-red-500" />
                <div>
                  <div className="text-red-700 dark:text-red-300 font-medium">
                    Error fetching node address
                  </div>
                  <div className="text-red-600 dark:text-red-400 text-sm">
                    {error?.message || 'Unknown error occurred'}
                  </div>
                </div>
              </div>
            )}

            {!isLoading && !isError && (
              <>
                {finalNodeAddress && finalNodeAddress.length > 0 ? (
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <CheckCircle2 size={20} className="text-green-500" />
                      <span className="text-green-700 dark:text-green-300 font-medium">
                        Node address found
                      </span>
                    </div>
                    <div className="flex items-center space-x-2 p-3 bg-white dark:bg-gray-800 rounded-lg">
                      <code className="flex-1 text-sm font-mono text-gray-900 dark:text-gray-100 break-all">
                        {finalNodeAddress}
                      </code>
                      <button
                        onClick={handleCopyNode}
                        className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
                        title="Copy node address"
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <AlertCircle size={20} className="text-gray-500" />
                    <div>
                      <div className="text-gray-700 dark:text-gray-300 font-medium">
                        No node address set
                      </div>
                      <div className="text-gray-600 dark:text-gray-400 text-sm">
                        This address has not registered a node address yet.
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Help Section */}
      {!queriedAddress && (
        <div className="mt-8 bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
            How to use Node Lookup
          </h3>
          <div className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
            <p>• Enter any Ethereum address to search for their registered node</p>
            <p>• Node addresses can be URLs, IP addresses, or any endpoint up to 64 characters</p>
            <p>• Results are fetched directly from the blockchain</p>
            <p>• Use the chain selector to search on different networks</p>
          </div>
        </div>
      )}
    </div>
  )
}
